/**
 * <s-product-gallery> — PDP gallery with a thumbnail rail and a swipeable main stage.
 *
 * Commands (commandfor points at this element's id):
 *   --select  on a [data-gallery-thumb][data-index] button shows that slide.
 *   --prev / --next on the rail arrows scroll the thumbnail list by one thumbnail.
 *
 * Required markup:
 *   [data-gallery-track]  horizontally scrolling stage, one [data-gallery-slide] per media.
 *   [data-gallery-thumbs] thumbnail list (vertical on desktop, horizontal on mobile).
 *   [data-gallery-thumb]  thumbnail buttons with data-index and data-media-id.
 *
 * Variant sync: when the product form partial re-renders, its [data-selected-media-id]
 * value is read and the matching slide is shown.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #observer;
  #resizeObserver;
  #track;
  #thumbList;
  #thumbs = [];
  #slides = [];
  #index = 0;
  #mediaId = '';

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.#track = this.querySelector('[data-gallery-track]');
    this.#thumbList = this.querySelector('[data-gallery-thumbs]');
    this.#thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    this.#slides = [...this.querySelectorAll('[data-gallery-slide]')];

    this.addEventListener('command', this.#handleCommand, opts);
    this.#track?.addEventListener('scroll', this.#handleScroll, { ...opts, passive: true });
    this.#track?.addEventListener('scrollend', this.#settle, opts);
    window.addEventListener('load', this.#settle, { ...opts, once: true });
    this.#thumbList?.addEventListener('scroll', this.#syncArrows, { ...opts, passive: true });
    this.#syncArrows();
    // Keep the current photo aligned when the stage width changes (rotation, window resize).
    this.#resizeObserver = new ResizeObserver(() => {
      const slide = this.#slides[this.#index];
      if (slide && this.#track) this.#track.scrollTo({ left: this.#index * this.#track.clientWidth, behavior: 'instant' });
      this.#syncArrows();
    });
    if (this.#track) this.#resizeObserver.observe(this.#track);
    this.#slides.forEach((slide, i) => slide.toggleAttribute('inert', i !== 0));

    const form = this.closest('[data-product-page]')?.querySelector('[data-product-form]');
    if (form) {
      this.#mediaId = form.querySelector('[data-selected-media-id]')?.dataset.selectedMediaId || '';
      const startIndex = this.#slides.findIndex((slide) => slide.dataset.mediaId === this.#mediaId);
      // Only jump on load when the link names a variant (for example ?variant=123); otherwise the first photo stays in place.
      const linkedVariant = new URL(window.location.href).searchParams.has('variant');
      if (linkedVariant && startIndex > 0) {
        this.#index = startIndex;
        requestAnimationFrame(() => {
          this.#track.scrollTo({ left: startIndex * this.#track.clientWidth, behavior: 'instant' });
          this.#setActive(startIndex);
        });
      }
      this.#observer = new MutationObserver(this.#handleFormUpdate);
      this.#observer.observe(form, { childList: true, subtree: true });
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    this.#resizeObserver?.disconnect();
  }

  #handleCommand = (event) => {
    if (event.command === '--select') {
      const index = Number(event.source?.dataset.index);
      if (Number.isInteger(index)) this.#select(index);
    } else if (event.command === '--prev' || event.command === '--next') {
      this.#stepThumbs(event.command === '--next' ? 1 : -1);
    }
  };

  #handleFormUpdate = () => {
    const form = this.closest('[data-product-page]')?.querySelector('[data-product-form]');
    const mediaId = form?.querySelector('[data-selected-media-id]')?.dataset.selectedMediaId || '';
    if (!mediaId || mediaId === this.#mediaId) return;
    this.#mediaId = mediaId;
    const index = this.#slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
    if (index >= 0) this.#select(index);
  };

  #select(index) {
    const slide = this.#slides[index];
    if (!slide || !this.#track) return;
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    this.#track.scrollTo({ left: index * this.#track.clientWidth, behavior });
    this.#setActive(index);
  }

  #setActive(index) {
    if (index === this.#index && this.#thumbs[index]?.getAttribute('aria-current') === 'true') return;
    this.#index = index;
    this.#thumbs.forEach((thumb, i) => {
      if (i === index) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    this.#slides.forEach((slide, i) => slide.toggleAttribute('inert', i !== index));
    this.#revealThumb(this.#thumbs[index]);
  }

  // Scrolls only the thumbnail list, never the page, so the active thumbnail stays visible.
  #revealThumb(thumb) {
    const list = this.#thumbList;
    if (!thumb || !list) return;
    const top = thumb.offsetTop - list.offsetTop;
    const left = thumb.offsetLeft - list.offsetLeft;
    if (top < list.scrollTop) list.scrollTop = top;
    else if (top + thumb.offsetHeight > list.scrollTop + list.clientHeight) list.scrollTop = top + thumb.offsetHeight - list.clientHeight;
    if (left < list.scrollLeft) list.scrollLeft = left;
    else if (left + thumb.offsetWidth > list.scrollLeft + list.clientWidth) list.scrollLeft = left + thumb.offsetWidth - list.clientWidth;
  }

  // Lands the stage on a whole photo if anything (lazy-image loading, programmatic scrolls) left it between two.
  #settle = () => {
    const track = this.#track;
    if (!track || track.clientWidth === 0) return;
    const index = Math.round(track.scrollLeft / track.clientWidth);
    const target = index * track.clientWidth;
    if (Math.abs(track.scrollLeft - target) > 2) track.scrollTo({ left: target, behavior: 'instant' });
    if (index !== this.#index) this.#setActive(index);
  };

  #handleScroll = () => {
    if (!this.#track || this.#track.clientWidth === 0) return;
    const index = Math.round(this.#track.scrollLeft / this.#track.clientWidth);
    if (index !== this.#index) this.#setActive(index);
  };

  #stepThumbs(direction) {
    const list = this.#thumbList;
    const first = this.#thumbs[0];
    if (!list || !first) return;
    const vertical = list.scrollHeight > list.clientHeight;
    const gap = parseFloat(getComputedStyle(list).rowGap) || 0;
    const step = (vertical ? first.offsetHeight : first.offsetWidth) + gap;
    list.scrollBy({ [vertical ? 'top' : 'left']: direction * step, behavior: 'smooth' });
  }

  #syncArrows = () => {
    const list = this.#thumbList;
    if (!list) return;
    const atStart = list.scrollTop <= 1;
    const atEnd = list.scrollTop + list.clientHeight >= list.scrollHeight - 1;
    this.querySelectorAll('[data-gallery-step="prev"]').forEach((b) => b.toggleAttribute('data-at-limit', atStart));
    this.querySelectorAll('[data-gallery-step="next"]').forEach((b) => b.toggleAttribute('data-at-limit', atEnd));
  };
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
